// ============================================================================
// CANCUN100 — CajasGemelas.js (Bloque 35)
// Fila de tres piezas: [ MXN · se guarda ] [ 1 USD = tasa ] [ USD · informativo ]
// Solo presentación: el padre sigue guardando el MXN (total / deposito) como hoy.
// La caja donde se escribió es la fuente; la otra se recalcula. MXN entero, USD centavos.
// ============================================================================
import React, { useEffect, useState } from 'react';
import { RADIO, RADIO_INSIGNIA, FUENTE } from './tema';

export const ALTO_CAJA_GEMELA = 44;

const num = (v) => { const n = parseFloat(String(v ?? '').replace(/[^0-9.]/g, '')); return Number.isFinite(n) ? n : 0; };
const fmtUsd = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false }).format(n || 0);
const fmtMxnVista = (n) => new Intl.NumberFormat('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
export const mxnDesdeUsd = (usd, tasa) => Math.round(num(usd) * (tasa > 0 ? tasa : 0));
export const usdDesdeMxn = (mxn, tasa) => (tasa > 0 ? num(mxn) / tasa : 0);

/**
 * Props
 *  - C: tema de colores de la app
 *  - etiqueta: 'Precio final' | 'Depósito'
 *  - mxn / setMxn: el valor MXN del padre (string, como hoy). Si setMxn es null → solo lectura.
 *  - tasa: tasa efectiva (número > 0). setTasa: si viene, la tasa es editable aquí (precargada con tasaDia).
 *  - tasaTexto: lo que el padre tiene guardado en texto ('' = usar la del día).
 *  - tasaDia: tasa del día (para precargar y como respaldo).
 *  - isMobile: apila las tres piezas.
 *  - placeholderMxn / inputMxnProps: extras para la caja MXN (min, required...).
 */
export default function CajasGemelas({ C, etiqueta, mxn, setMxn, tasa, setTasa, tasaTexto, tasaDia, isMobile, placeholderMxn = 'Precio total', inputMxnProps = {}, notaTasa }) {
  const soloLectura = typeof setMxn !== 'function';
  const [fuente, setFuente] = useState('mxn');     // 'mxn' | 'usd' — la última caja donde se escribió
  const [usdTexto, setUsdTexto] = useState('');     // lo escrito en USD (cuando la fuente es USD)
  const tasaOk = tasa > 0 ? tasa : 0;

  // Si cambia la tasa y la fuente es USD, se recalcula el MXN (la caja que NO se escribió).
  useEffect(() => {
    // Si el padre ya vació el MXN (guardado / vaciar formulario), no se repone nada.
    if (soloLectura || fuente !== 'usd' || usdTexto === '' || mxn === '' || mxn == null) return;
    setMxn(String(mxnDesdeUsd(usdTexto, tasaOk)));
  }, [tasaOk]); // eslint-disable-line

  // Si el padre vacía el MXN (vaciar formulario / borrador), la fuente vuelve a MXN.
  useEffect(() => { if (mxn === '' || mxn == null) { setFuente('mxn'); setUsdTexto(''); } }, [mxn]);

  const usdVista = fuente === 'usd' ? usdTexto : (mxn === '' || mxn == null ? '' : fmtUsd(usdDesdeMxn(mxn, tasaOk)));
  const mxnVista = soloLectura ? fmtMxnVista(mxn) : (mxn ?? '');

  const onMxn = (e) => { setFuente('mxn'); setUsdTexto(''); setMxn(e.target.value); };
  const onUsd = (e) => {
    const v = e.target.value;
    setFuente('usd'); setUsdTexto(v);
    setMxn(v === '' ? '' : String(mxnDesdeUsd(v, tasaOk)));
  };
  const onTasa = (e) => setTasa && setTasa(e.target.value);

  const calcMxn = !soloLectura && fuente === 'usd';
  const calcUsd = soloLectura || fuente === 'mxn';
  const pill = (texto, color) => (
    <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 6px', borderRadius: RADIO_INSIGNIA, letterSpacing: '.04em', color, background: `${color}1f`, border: `1px solid ${color}55`, whiteSpace: 'nowrap' }}>{texto}</span>
  );
  const etiquetaStyle = { fontSize: 12, fontWeight: 500, color: 'var(--au-text-faint)', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 } // APP 2/22: las insignias bajan de renglón en móvil;
  const caja = (color, calculada, lectura) => ({
    display: 'flex', alignItems: 'center', gap: 6, height: ALTO_CAJA_GEMELA, boxSizing: 'border-box', padding: '0 12px',
    borderRadius: RADIO, border: `1px solid ${color}${lectura ? '33' : '66'}`,
    background: calculada || lectura ? `${C.surface}99` : C.surface, opacity: calculada ? 0.85 : 1,
  });
  const input = (lectura) => ({
    border: 0, background: 'transparent', color: C.text, fontFamily: FUENTE, fontSize: 22, fontWeight: 700, width: '100%', minWidth: 0, outline: 'none',
    fontVariantNumeric: 'tabular-nums', cursor: lectura ? 'default' : 'text',
  });
  const simbolo = { color: C.muted, fontWeight: 600, fontSize: 14, flexShrink: 0 };
  const tasaEditable = typeof setTasa === 'function';
  const tasaVista = tasaEditable ? (tasaTexto !== '' && tasaTexto != null ? tasaTexto : String(tasaDia ?? '')) : fmtUsd(tasaOk);

  return (
    <div style={{ marginBottom: 12 }}>
      <div className="c100-cajas" style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'minmax(0,1fr) auto minmax(0,1fr)', gap: 10, alignItems: 'end' }}>{/* APP 2/22: en ≤ 480 px una columna por CSS (App.js no pasa isMobile) */}
        <div style={{ minWidth: 0 }}>
          <div style={etiquetaStyle}><span>{etiqueta}{inputMxnProps.required && <span style={{ color: 'var(--au-error-fg)' }}> *</span>}</span>{pill('MXN · se guarda', C.green)}</div>
          <div style={caja(C.green, calcMxn, soloLectura)}>
            <span style={simbolo}>$</span>
            {soloLectura
              ? <span style={input(true)}>{mxnVista}</span>
              : <input type="number" min="0" inputMode="decimal" placeholder={placeholderMxn} value={mxnVista} onChange={onMxn} className="au-input-gemela" style={input(false)} {...inputMxnProps} />}
          </div>
        </div>
        <div style={{ textAlign: 'center', minWidth: 0 }}>
          <div style={{ ...etiquetaStyle, justifyContent: 'center' }}><span>1 USD =</span></div>
          <div style={{ ...caja(C.border, !tasaEditable, !tasaEditable), width: isMobile ? '100%' : 120, justifyContent: 'center', padding: '0 10px' }}>
            {tasaEditable
              ? <input type="number" min="0" step="0.01" inputMode="decimal" value={tasaVista} onChange={onTasa} style={{ ...input(false), fontSize: 14, textAlign: 'center' }} />
              : <span style={{ ...input(true), fontSize: 14, textAlign: 'center' }}>{tasaVista}</span>}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 3, whiteSpace: 'nowrap' }}>{notaTasa || (tasaEditable ? 'MXN · tasa del día, editable' : 'misma tasa de arriba')}</div>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={etiquetaStyle}><span>Equivalente</span>{pill('USD · informativo', C.blue)}</div>
          <div style={caja(C.blue, calcUsd && !soloLectura, soloLectura)}>
            <span style={simbolo}>US$</span>
            {soloLectura
              ? <span style={input(true)}>{usdVista}</span>
              : <input type="text" inputMode="decimal" placeholder="0.00" value={usdVista} onChange={onUsd} style={input(false)} />}
          </div>
        </div>
      </div>
      {!soloLectura && (
        <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
          {fuente === 'usd' ? 'Último que escribiste: USD → el MXN se calcula.' : 'Último que escribiste: MXN → el USD se calcula.'}
        </div>
      )}
    </div>
  );
}
